import { OFFER_SMS_CONSENT_DESCRIPTION } from '../../consent-copy';
import type { SmsConsentSpec } from './smsConsent';

/**
 * The account-related SMS disclosure. Disclosure constants are versionless
 * because each surface's registry owns the audit key for its presentation.
 */
export const ACCOUNT_SMS_CONSENT_DESCRIPTION =
  'By providing your phone number and checking this box, I agree to receive account-related text messages from Opendoor at the number provided, including offer status updates, transaction alerts, and support notices. Message frequency varies. Message and data rates may apply. Reply HELP for help or STOP to cancel. Consent is not a condition of purchase.';

/**
 * The carrier boilerplate both descriptions end with, written out again rather
 * than composed into them: the descriptions stay verbatim literals so they can
 * be read straight against what legal approved, and nothing about this constant
 * can silently rewrite a version's copy. `smsConsent.test.ts` pins it as a
 * suffix of both, so the two can never drift apart unnoticed.
 *
 * It is the run each `marketing_sms_v3` box folds behind "more" — identical on
 * both, and the part that buries the actual consent when boxes stack.
 */
export const SMS_CARRIER_BOILERPLATE =
  'Message frequency varies. Message and data rates may apply. Reply HELP for help or STOP to cancel. Consent is not a condition of purchase.';

/**
 * The account form's marketing-SMS consent versions, keyed by the
 * `consentLanguage` value recorded for each. `version` names the descriptions
 * only — the bold headers are this form's own presentation of them, added when
 * the copy moved into the `MarketingOptIn` label/description shape.
 *
 * DO NOT EDIT AN EXISTING ENTRY. Historical `Consent` rows reference these keys,
 * so revising copy means adding a new entry and pointing `CURRENT_VERSION` at
 * it. Retired entries stay here so a row's key can still be resolved to the copy
 * it was written under.
 */
const SMS_CONSENT_VERSIONS = {
  /**
   * The immutable disclosure named by `marketing_sms_account_form_v1`, retained
   * so rows carrying that key remain resolvable to their recorded copy.
   */
  marketing_sms_account_form_v1: {
    checkboxes: [
      {
        name: 'marketing-opt-in',
        label: 'Opt in to marketing communications',
        description:
          'I consent to receive marketing calls and texts from Opendoor and its affiliates to the number provided. I understand that I may opt out at any time and consent is not a condition of purchase.',
        analyticsSuffix: 'marketing-opt-in',
        drivesPreference: true,
      },
    ],
  },
  /**
   * What the V2 form renders: a single offer/promotional opt-in, which is also
   * the box that drives `marketingSmsOptOut`. One disclosure where the
   * cinderblocks arm of the `auth-fe-account-form` flag shows two — a deliberate
   * divergence documented in `PARITY.md`. The distinct version key keeps each
   * arm's `Consent` rows naming the copy that arm actually showed.
   */
  marketing_sms_account_form_v2: {
    checkboxes: [
      {
        name: 'marketing-opt-in',
        label: 'Opt in to marketing communications',
        description: OFFER_SMS_CONSENT_DESCRIPTION,
        analyticsSuffix: 'marketing-opt-in',
        drivesPreference: true,
      },
    ],
  },
  /**
   * The dual account-vs-offer opt-in the cinderblocks arm renders and records,
   * split that way for Twilio RCS. Kept here so the key stays resolvable to its
   * copy and can never be reused for different words; the V2 form does not
   * render it. Only the offer box drives `marketingSmsOptOut` — the account box
   * is display-only until the RCS write lands.
   */
  marketing_sms_v3: {
    checkboxes: [
      {
        name: 'account-sms-opt-in',
        label: 'Opt in to account-related texts',
        description: ACCOUNT_SMS_CONSENT_DESCRIPTION,
        collapsedTail: SMS_CARRIER_BOILERPLATE,
        analyticsSuffix: 'account-sms-checkbox',
      },
      {
        name: 'offer-sms-opt-in',
        label: 'Opt in to offer-related and promotional texts',
        description: OFFER_SMS_CONSENT_DESCRIPTION,
        collapsedTail: SMS_CARRIER_BOILERPLATE,
        analyticsSuffix: 'offer-sms-checkbox',
        drivesPreference: true,
      },
    ],
  },
} as const satisfies Record<string, Omit<SmsConsentSpec, 'version'>>;

type SmsConsentVersion = keyof typeof SMS_CONSENT_VERSIONS;

const CURRENT_VERSION: SmsConsentVersion = 'marketing_sms_account_form_v2';

/**
 * The consent `RegistrationModule` renders when a surface passes none, and the
 * version the registration write records for it.
 *
 * This is the account form's own consent. A surface that records under a
 * different version — reception-fe's `marketing_sms_reception_v1` — passes its
 * own spec instead, and the recorded `consentLanguage` follows the spec that
 * rendered rather than this default, so the two can never drift.
 */
export const ACCOUNT_FORM_SMS_CONSENT: SmsConsentSpec = {
  version: CURRENT_VERSION,
  ...SMS_CONSENT_VERSIONS[CURRENT_VERSION],
};
